import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';

/**
 * "Notify Me at Launch" CTA.
 *
 * Behaviour:
 *  - Signed-in: POSTs directly to /api/notify-me, never redirects to /login.
 *    Renders "You're on the list" once opted in (also reflects state from
 *    /api/notify-me/status on mount so refreshes don't lose the success state).
 *  - Signed-out: redirects to /register?notify=1&next=<current path>. After
 *    successful register/login the auth flow auto-completes the opt-in (see
 *    `useAutoCompleteLaunchOptIn`) and lands the user back on this page.
 */
const NotifyMeButton = ({ source = 'homepage_launch_cta', className = '', testId = 'notify-me-button' }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // On mount (when signed in), check current opt-in status so the button
  // renders correctly after a refresh.
  useEffect(() => {
    if (authLoading || !user) return;
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/notify-me/status`, { withCredentials: true })
      .then((r) => { if (active) setSubscribed(!!r.data?.subscribed); })
      .catch(() => {});
    return () => { active = false; };
  }, [authLoading, user]);

  const optIn = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/notify-me`,
        { source },
        { withCredentials: true },
      );
      setSubscribed(true);
      if (r.data?.status === 'already_subscribed') {
        toast.success("You're already on the list — we'll notify you when bookings open.");
      } else {
        toast.success("You're on the list — we'll notify you when the first event on VinePass launches.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your preference. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    if (authLoading) return;
    if (!user) {
      // Anonymous → send to register with a return path + intent flag
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/register?notify=1&next=${next}`);
      return;
    }
    if (subscribed) return; // no-op; already on the list
    optIn();
  };

  return (
    <Button
      onClick={handleClick}
      disabled={submitting || subscribed}
      className={
        subscribed
          ? `bg-emerald-700 text-white hover:bg-emerald-800 rounded-full px-8 py-6 font-medium shadow-md ${className}`
          : `bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8 py-6 font-medium shadow-md ${className}`
      }
      data-testid={testId}
      aria-pressed={subscribed}
    >
      {submitting ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Adding you to the list…
        </>
      ) : subscribed ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          You&rsquo;re on the list
        </>
      ) : (
        <>
          <Bell className="w-4 h-4 mr-2" />
          Notify Me at Launch
        </>
      )}
    </Button>
  );
};

export default NotifyMeButton;
